import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { transform } from "next/dist/build/swc/generated-native";

gsap.registerPlugin(ScrollTrigger);

const bgImages = [
  { src: "/images/student/icon10.png", style: { top: '10%', left: '1%', width: '240px', height: '240px', transform: 'rotate(-15deg)' } },
  { src: "/images/student/icon2.png", style: { bottom: '-13.25%', left: '-7.50%', width: '470px', height: '470px', transform: 'rotate(10deg)', transformOrigin: 'center center' } },
  { src: "/images/student/icon3.png", style: { top: '13%', left: '18%', width: '136px', height: '136px' } },
  { src: "/images/student/icon4.png", style: { top: '2%', right: '-5.50%', width: '274px', height: '274px', transform: 'rotate(-15deg)', transformOrigin: 'right bottom' } },
  { src: "/images/student/icon5.png", style: { bottom: '18.75%', left: '18%', width: '140px', height: '140px', transformOrigin: 'center center', transform: 'rotate(-15deg)' } },
  { src: "/images/student/icon6.png", style: { top: '50%', right: '8.00%', transform: 'translateY(-50%)', width: '222px', height: '222px', transformOrigin: 'right center' } },
  { src: "/images/student/icon7.png", style: { bottom: '-8.75%', right: '-0.75%', width: '344px', height: '344px', transform: 'rotate(-15deg)', transformOrigin: 'left top', } },
  { src: "/images/student/icon8.png", style: { top: '11%', right: '20%', width: '140px', height: '140px', transformOrigin: 'top left', transform: 'rotate(7deg)' } },
  { src: "/images/student/icon9.png", style: { top: '50%', left: '8%', transform: 'translateY(-50%)', width: '200px', height: '200px', transformOrigin: 'left center' } },
  { src: "/images/student/icon1.png", style: { bottom: '12.75%', right: '22.75%', width: '200px', height: '200px', transform: 'rotate(-15deg)', transformOrigin: 'center center' } },
];

const originTransforms = [
  { x: 0, y: 0, scale: 1, rotate: -15 },
  { x: 0, y: 0, scale: 1, rotate: 10, },
  { x: 0, y: 0, scale: 1, rotate: 0 },
  { x: 0, y: 0, scale: 1, rotate: -15 },
  { x: 0, y: 0, scale: 1, rotate: -15 },
  { x: 0, y: 0, scale: 1, rotate: 0 },
  { x: 0, y: 0, scale: 1, rotate: -15 },
  { x: 0, y: 0, scale: 1, rotate: 7 },
  { x: 0, y: 0, scale: 1, rotate: 0 },
  { x: 0, y: 0, scale: 1, rotate: -15 },
];
const targetTransforms = [
  { x: 0, y: 0, scale: 0.8, rotate: -25, },
  { x: -50, y: 50, scale: 0.6, rotate: 10 },
  { x: 0, y: -40, scale: 0.6, rotate: 0 },
  { x: -30, y: -60, scale: 0.6, rotate: -30, },
  { x: 0, y: 80, scale: 0.8, rotate: 10 },
  { x: 90, y: 0, scale: 0.8, rotate: 0 },
  { x: 137, y: 100, scale: 0.6, rotate: -15 },
  { x: 0, y: 0, scale: 1, rotate: 30 },
  { x: -90, y: 0, scale: 0.8, rotate: 0 },
  { x: 0, y: 80, scale: 0.8, rotate: 10 },
];

export default function BgFloatImages({ sectionIndex }: { sectionIndex: number }) {
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);

  // 晃动动画
  const startWobble = (idx: number) => {
    const el = imgRefs.current[idx];
    if (!el) return;
    gsap.to(el, {
      x: `+=${Math.random() > 0.5 ? 15 : -15}`,
      y: `+=${Math.random() > 0.5 ? 15 : -15}`,
      duration: 1.5 + Math.random(),
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
  };

  // 停止晃动动画
  const stopWobble = (idx: number) => {
    const el = imgRefs.current[idx];
    if (!el) return;
    gsap.killTweensOf(el);
  };

  // 切换 section 时，平滑移动并缩放，结束后继续晃动
  useEffect(() => {
    bgImages.forEach((_, i) => {
      startWobble(i)
    });
  }, []);

  useEffect(() => {
    bgImages.forEach((_, i) => {
      const el = imgRefs.current[i];
      if (!el) return;

      gsap.fromTo(
        el,
        { ...originTransforms[i] },
        {
          ...targetTransforms[i],
          opacity: 0.5,
          scrollTrigger: {
            trigger: ".section-2",
            start: "top bottom",
            end: "top top",
            scrub: true,
            onUpdate: (self) => {
              const progress = self.progress;
              // 线性插值每个属性
              const from = originTransforms[i];
              const to = targetTransforms[i];
              gsap.to(el, {
                x: from.x + (to.x - from.x) * progress,
                y: from.y + (to.y - from.y) * progress,
                scale: from.scale + (to.scale - from.scale) * progress,
                rotate: from.rotate + (to.rotate - from.rotate) * progress,
                // opacity: from.opacity + (to.opacity - from.opacity) * progress,
                duration: 0.1,
                overwrite: "auto"
              });
              if (progress == 0 || progress == 1) {
                startWobble(i);
              }
            }
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none z-0">
      {bgImages.map((img, i) => (
        <img
          key={i}
          ref={el => { imgRefs.current[i] = el; }}
          src={img.src}
          style={{
            position: "fixed",
            zIndex: 0,
            transformOrigin: 'left bottom',
            willChange: 'transform',
            ...img.style,
          }}
          alt=""
        />
      ))}
    </div>
  );
}
